import React, { useContext, useState, useRef, useEffect } from 'react';
import {
  FONT_DEFAULT_FAMILY,
  FONT_DEFAULT_SIZE,
  FONT_DEFAULT_COLOR,
  LINE_HEIGHT_MULT,
} from 'constants/fonts';
import { CanvasContext } from 'components/upload-image/CanvasContext';
import { sendToPanorama, saveClicked } from 'utils/sendToPanorama';
import { logFilestackUpload, getFilestackFileData } from 'utils/logImages';

export default function ImageActionButton({
  buttonRef,
  imageRef,
  canvasRef,
  textItems,
  hasAddedTextRef,
  fileName,
}) {
  const {
    brightness,
    contrast,
    saturation,
    hueRotate,
    blur,
    isBlackAndWhite,
    isVintage,
    isWarmGlow,
    isCoolTone,
    isFadedFilm,
    isSepia,
    isSoftPastel,
    isVibrantPop,
    isCinematic,
    isNaturalEnhance,
    isBright,
    isClear,
    canvasRotationDeg,
    requestRotateExit,

    // ✅ add stickers to export
    stickerItems,
  } = useContext(CanvasContext);

  const [uploadProgress, setUploadProgress] = useState(null);
  const [displayProgress, setDisplayProgress] = useState(0);
  const [uploadError, setUploadError] = useState(false);

  const uploadStartTimeRef = useRef(null);

  useEffect(() => {
    if (uploadProgress === null) return;

    const interval = setInterval(() => {
      setDisplayProgress((prev) => {
        // Once upload is done, move smoothly to 100
        if (uploadProgress >= 100) {
          return Math.min(prev + 3, 100);
        }

        // Fast-ish progress up to 80
        if (prev < 80) {
          return prev + 2;
        }

        // Slower progress from 80 to 98
        if (prev < 98) {
          return prev + 1;
        }

        // Hold at 98 until upload is actually complete
        return prev;
      });
    }, 60);

    return () => clearInterval(interval);
  }, [uploadProgress]);

  const clamp = (v, min = 0, max = 255) => Math.max(min, Math.min(max, v));

  const exportedFilename = () => {
    if (!fileName) return 'edited-image.jpg';

    const dotIndex = fileName.lastIndexOf('.');
    const name = dotIndex === -1 ? fileName : fileName.slice(0, dotIndex);

    return `${name}-edited.jpg`;
  };

  const getFormatFromName = () => {
    return { mime: 'image/jpeg', quality: 0.92 };
  };

  // Must match CanvasContext preview e.g. blur(${blur * 0.2}px)
  const BLUR_UI_TO_CSS_MULTIPLIER = 0.2;

  const getPxPerCssPx = (liveCanvas) => {
    if (!liveCanvas) return 1;
    const rect = liveCanvas.getBoundingClientRect();
    if (!rect || rect.width <= 0) return 1;
    return liveCanvas.width / rect.width;
  };

  const getScaleXY = (liveCanvas) => {
    if (!liveCanvas) return { scaleX: 1, scaleY: 1 };
    const rect = liveCanvas.getBoundingClientRect?.();
    if (rect?.width > 0 && rect?.height > 0) {
      return {
        scaleX: liveCanvas.width / rect.width,
        scaleY: liveCanvas.height / rect.height,
      };
    }
    // Fallback: best-effort using offset sizes
    const cssW = liveCanvas.offsetWidth || 0;
    const cssH = liveCanvas.offsetHeight || 0;
    if (cssW > 0 && cssH > 0) {
      return {
        scaleX: liveCanvas.width / cssW,
        scaleY: liveCanvas.height / cssH,
      };
    }
    return { scaleX: 1, scaleY: 1 };
  };

  // Same padding as textarea in TextTool.jsx (px)
  const PAD_X = 8; // total L+R
  const PAD_Y = 8; // total T+B

  // ---- Text drawing helpers (export) ----
  const resolveFont = (item = {}, pxPerCssPx = 1) => {
    const size = (item.fontSize || FONT_DEFAULT_SIZE) * pxPerCssPx;
    const family = item.fontFamily || FONT_DEFAULT_FAMILY;
    const weight = item.fontWeight || 'normal';
    const style = item.fontStyle || 'normal';
    return `${style} ${weight} ${size}px ${family}`;
  };

  const getTextItemsArray = () => {
    if (Array.isArray(textItems)) return textItems;
    if (textItems && Array.isArray(textItems.current)) return textItems.current; // ref
    return [];
  };

  /**
   * ensure web fonts are loaded before drawing to export canvas
   */
  const ensureFontsReadyForExport = async (pxPerCssPx = 1) => {
    try {
      const items = getTextItemsArray();
      if (!document.fonts || items.length === 0) return;

      const fontStrings = new Set();
      items.forEach((item) => {
        if (!item || !item.text) return;
        fontStrings.add(resolveFont(item, pxPerCssPx));
      });

      await Promise.all(
        Array.from(fontStrings).map((fontStr) => document.fonts.load(fontStr)),
      );

      await document.fonts.ready;
    } catch (e) {}
  };

  const hexToRgba = (hex, alpha = 1) => {
    if (!hex) return `rgba(0, 0, 0, ${alpha})`;

    let h = String(hex).replace('#', '').trim();

    if (h.length === 3) {
      h = h
        .split('')
        .map((c) => c + c)
        .join('');
    }

    if (h.length !== 6) return `rgba(0, 0, 0, ${alpha})`;

    const r = parseInt(h.slice(0, 2), 16);
    const g = parseInt(h.slice(2, 4), 16);
    const b = parseInt(h.slice(4, 6), 16);

    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  const drawTextItems = (ctx, liveCanvas) => {
    const items = getTextItemsArray();
    if (items.length === 0) return;

    const { scaleX, scaleY } = getScaleXY(liveCanvas);

    const rawCanvasDeg = Number(canvasRotationDeg) || 0;
    const canvasDegNorm = ((rawCanvasDeg % 360) + 360) % 360; // 0..359

    const measureDescPad = (lines, fontPx) => {
      const fallbackDescent = Math.ceil(fontPx * 0.28);
      let maxDescent = fallbackDescent;

      for (let i = 0; i < lines.length; i++) {
        const m = ctx.measureText(lines[i] || '');
        const d =
          typeof m.actualBoundingBoxDescent === 'number'
            ? m.actualBoundingBoxDescent
            : fallbackDescent;
        if (d > maxDescent) maxDescent = d;
      }

      return Math.ceil(maxDescent) + 2;
    };

    items.forEach((item) => {
      if (!item || !item.text) return;

      const {
        x = 0,
        y = 0,
        color = FONT_DEFAULT_COLOR,
        width = 0,
        height = 0,
        opacity = 1,
        flipX = false,
        flipY = false,

        hasOutline = false,
        outlineColor = '#000000',
        outlineWidth = 2,

        hasShadow = false,
        shadowColor = '#000000',
        shadowBlur = 4,
        shadowOffsetX = 2,
        shadowOffsetY = 2,
        shadowOpacity = 1,
      } = item;

      const align = item.textAlign ?? 'left';
      const lines = String(item.text).split('\n');

      const family = item.fontFamily || FONT_DEFAULT_FAMILY;
      const weight = item.fontWeight || 'normal';
      const style = item.fontStyle || 'normal';

      const itemDegRaw = Number(item.rotation ?? 0);
      const totalDeg = canvasDegNorm + itemDegRaw;
      const effectiveDeg = ((totalDeg % 360) + 360) % 360;

      const isQuarterTurn = effectiveDeg === 90 || effectiveDeg === 270;
      const isEffective270 = effectiveDeg === 270;

      const shouldInvertRotation = Boolean(flipX) !== Boolean(flipY);
      const totalDegSigned = shouldInvertRotation ? -totalDeg : totalDeg;

      // ✅ Match preview logic: keep the text box dimensions unchanged.
      // The DOM preview rotates the wrapper, but does not swap width/height.
      const wCss = width;
      const hCss = height;

      const wCanvas = (wCss || 0) * scaleX;
      const hCanvas = (hCss || 0) * scaleY;

      const baseFontCss =
        typeof item.fontSize === 'number' ? item.fontSize : FONT_DEFAULT_SIZE;
      const fontPx = baseFontCss * scaleY;
      const lineH = fontPx * LINE_HEIGHT_MULT;

      const effectScale = (scaleX + scaleY) / 2;
      const outlineWidthPx = Math.max(
        0,
        (typeof outlineWidth === 'number' ? outlineWidth : 2) * effectScale,
      );
      const shadowBlurPx = Math.max(
        0,
        (typeof shadowBlur === 'number' ? shadowBlur : 4) * effectScale,
      );

      const rawShadowOffsetX =
        (typeof shadowOffsetX === 'number' ? shadowOffsetX : 2) * scaleX;
      const rawShadowOffsetY =
        (typeof shadowOffsetY === 'number' ? shadowOffsetY : 2) * scaleY;

      // Shadow direction needs to follow the same visual transform as the text
      const localShadowX = (flipX ? -1 : 1) * rawShadowOffsetX;
      const localShadowY = (flipY ? -1 : 1) * rawShadowOffsetY;

      const shadowRad = (totalDegSigned * Math.PI) / 180;
      const cosShadow = Math.cos(shadowRad);
      const sinShadow = Math.sin(shadowRad);

      const shadowOffsetXPx =
        localShadowX * cosShadow - localShadowY * sinShadow;
      const shadowOffsetYPx =
        localShadowX * sinShadow + localShadowY * cosShadow;

      ctx.save();
      ctx.globalAlpha = typeof opacity === 'number' ? opacity : 1;
      ctx.font = `${style} ${weight} ${fontPx}px ${family}`.trim();
      ctx.fillStyle = color;

      ctx.strokeStyle = outlineColor || '#000000';
      ctx.lineWidth = outlineWidthPx;
      ctx.lineJoin = 'round';
      ctx.miterLimit = 2;

      ctx.textAlign = align;
      ctx.textBaseline = 'top';

      const descPad = measureDescPad(lines, fontPx);

      const padTop = (PAD_Y / 2) * scaleY;
      const padLeft = (PAD_X / 2) * scaleX + (isEffective270 ? descPad : 0);
      const padRight =
        (PAD_X / 2) * scaleX + (!isEffective270 && isQuarterTurn ? descPad : 0);

      const innerWidth = Math.max(0, wCanvas - padLeft - padRight);

      const boxLeft = x - wCanvas / 2;
      const boxTop = y - hCanvas / 2.5;

      const isDrawnPos90 =
        Math.round(((totalDegSigned % 360) + 360) % 360) === 90 ||
        Math.round(totalDegSigned) === 90;

      const isDrawnNeg90 =
        Math.round(((totalDegSigned % 360) + 360) % 360) === 270 ||
        Math.round(totalDegSigned) === -90;

      const pos90SnapXOffset = isDrawnPos90 ? 4 * scaleX : 0;
      const neg90SnapXOffset = isDrawnNeg90 ? -14 * scaleX : 0;
      const quarterTurnXOffset = pos90SnapXOffset + neg90SnapXOffset;

      let anchorX = boxLeft + padLeft + quarterTurnXOffset;
      if (align === 'center')
        anchorX = boxLeft + padLeft + innerWidth / 2 + quarterTurnXOffset;
      if (align === 'right')
        anchorX = boxLeft + padLeft + innerWidth + quarterTurnXOffset;

      const textTop = boxTop + padTop;

      if (totalDegSigned || flipX || flipY) {
        ctx.translate(x, y);
        if (totalDegSigned) ctx.rotate((totalDegSigned * Math.PI) / 180);
        if (flipX || flipY) ctx.scale(flipX ? -1 : 1, flipY ? -1 : 1);
        ctx.translate(-x, -y);
      }

      if (hasShadow) {
        ctx.shadowColor = hexToRgba(shadowColor, shadowOpacity);
        ctx.shadowBlur = shadowBlurPx;
        ctx.shadowOffsetX = shadowOffsetXPx;
        ctx.shadowOffsetY = shadowOffsetYPx;
      } else {
        ctx.shadowColor = 'transparent';
        ctx.shadowBlur = 0;
        ctx.shadowOffsetX = 0;
        ctx.shadowOffsetY = 0;
      }

      for (let i = 0; i < lines.length; i++) {
        const line = lines[i];
        const yPos = textTop + i * lineH;

        // Draw outline with no shadow so the shadow is not doubled
        if (hasOutline && outlineWidthPx > 0) {
          const prevShadowColor = ctx.shadowColor;
          const prevShadowBlur = ctx.shadowBlur;
          const prevShadowOffsetX = ctx.shadowOffsetX;
          const prevShadowOffsetY = ctx.shadowOffsetY;

          ctx.shadowColor = 'transparent';
          ctx.shadowBlur = 0;
          ctx.shadowOffsetX = 0;
          ctx.shadowOffsetY = 0;

          ctx.strokeText(line, anchorX, yPos);

          ctx.shadowColor = prevShadowColor;
          ctx.shadowBlur = prevShadowBlur;
          ctx.shadowOffsetX = prevShadowOffsetX;
          ctx.shadowOffsetY = prevShadowOffsetY;
        }

        // Draw fill with the real shadow once
        ctx.fillText(line, anchorX, yPos);
      }

      ctx.restore();
    });
  };

  // ---- Sticker drawing helpers (export) ----
  const getStickerItemsArray = () => {
    if (Array.isArray(stickerItems)) return stickerItems;
    if (stickerItems && Array.isArray(stickerItems.current))
      return stickerItems.current; // ref
    return [];
  };

  const withPublicUrl = (path = '') =>
    path.startsWith('http') ? path : `${process.env.PUBLIC_URL || ''}${path}`;

  const loadImage = (src) =>
    new Promise((resolve, reject) => {
      const img = new Image();
      // Same-origin in CRA/public; harmless and future-proof if moved to CDN.
      img.crossOrigin = 'anonymous';
      img.onload = () => resolve(img);
      img.onerror = (e) => reject(e);
      img.src = src;

      // If supported, decode for more reliable render timing.
      if (img.decode) {
        img
          .decode()
          .then(() => resolve(img))
          .catch(() => {
            // ignore decode failures; onload will still resolve
          });
      }
    });

  const drawStickerItems = async (ctx) => {
    const items = getStickerItemsArray();
    if (!items.length) return;

    const cache = new Map();

    const ensure = async (src) => {
      if (!src) return null;
      if (cache.has(src)) return cache.get(src);

      try {
        const img = await loadImage(withPublicUrl(src));
        cache.set(src, img);
        return img;
      } catch {
        console.warn('Failed to load sticker for export:', src);
        cache.set(src, null);
        return null;
      }
    };

    const uniqueSrc = Array.from(
      new Set(items.map((s) => s?.src).filter(Boolean)),
    );

    await Promise.all(uniqueSrc.map((src) => ensure(src)));

    items.forEach((s) => {
      if (!s?.src) return;

      const img = cache.get(s.src);
      if (!img) return;

      const x = typeof s.x === 'number' ? s.x : 0;
      const y = typeof s.y === 'number' ? s.y : 0;
      const w =
        typeof s.w === 'number' ? s.w : img.naturalWidth || img.width || 0;
      const h =
        typeof s.h === 'number' ? s.h : img.naturalHeight || img.height || 0;

      const rotation = Number(s.rotation || 0);
      const flipX = Boolean(s.flipX);
      const flipY = Boolean(s.flipY);

      if (!w || !h) return;

      ctx.save();

      // Match the live sticker preview behaviour
      ctx.translate(x, y);
      ctx.scale(flipX ? -1 : 1, flipY ? -1 : 1);
      ctx.rotate((rotation * Math.PI) / 180);
      ctx.drawImage(img, -w / 2, -h / 2, w, h);

      ctx.restore();
    });
  };

  // ---- Filter maths (export) ----
  const getHueRotateMatrix = (deg) => {
    const rad = (deg * Math.PI) / 180;
    const cos = Math.cos(rad);
    const sin = Math.sin(rad);

    const a00 = 0.213 + cos * 0.787 - sin * 0.213;
    const a01 = 0.715 - cos * 0.715 - sin * 0.715;
    const a02 = 0.072 - cos * 0.072 + sin * 0.928;

    const a10 = 0.213 - cos * 0.213 + sin * 0.143;
    const a11 = 0.715 + cos * 0.285 + sin * 0.14;
    const a12 = 0.072 - cos * 0.072 - sin * 0.283;

    const a20 = 0.213 - cos * 0.213 - sin * 0.787;
    const a21 = 0.715 - cos * 0.715 + sin * 0.715;
    const a22 = 0.072 + cos * 0.928 + sin * 0.072;

    return [a00, a01, a02, a10, a11, a12, a20, a21, a22];
  };

  const applyHueRotateMatrix = (r, g, b, m) => {
    const nr = r * m[0] + g * m[1] + b * m[2];
    const ng = r * m[3] + g * m[4] + b * m[5];
    const nb = r * m[6] + g * m[7] + b * m[8];
    return [nr, ng, nb];
  };

  const applyFiltersToCanvas = (ctx, width, height) => {
    let brightFactor =
      typeof brightness === 'number' && brightness > 0 ? brightness : 1;
    let contrastFactor =
      typeof contrast === 'number' && contrast > 0 ? contrast : 1;
    let satFactor =
      typeof saturation === 'number' && saturation >= 0 ? saturation : 1;
    let hueDegrees = typeof hueRotate === 'number' ? hueRotate : 0;

    let fadedSepiaAmount = 0;
    let liftBlacksAmount = 0;

    let softPastelLiftAmount = 0;
    let softPastelWashAmount = 0;

    let vibrantPopVibrance = 0;

    if (isVintage) {
      brightFactor *= 1.02;
      contrastFactor *= 1.05;
      satFactor *= 0.9;
      hueDegrees += -5;
    }

    if (isWarmGlow) {
      brightFactor *= 1.05;
      contrastFactor *= 0.98;
      satFactor *= 1.15;
      hueDegrees += 8;
    }

    if (isCoolTone) {
      brightFactor *= 1.0;
      contrastFactor *= 1.07;
      satFactor *= 0.7;
      hueDegrees += -42;
    }

    if (isFadedFilm) {
      brightFactor *= 1.03;
      contrastFactor *= 0.85;
      satFactor *= 0.85;
      hueDegrees += -2;

      fadedSepiaAmount = 0.08;
      liftBlacksAmount = 1;
    }

    if (isSoftPastel) {
      brightFactor *= 1.08;
      contrastFactor *= 0.82;
      satFactor *= 0.72;
      hueDegrees += -6;

      softPastelLiftAmount = 1;
      softPastelWashAmount = 0.005;
    }

    if (isVibrantPop) {
      brightFactor *= 1.04;
      contrastFactor *= 1.14;
      satFactor *= 1.35;
      hueDegrees += 2;

      vibrantPopVibrance = 0.45;
    }

    if (isCinematic) {
      brightFactor *= 0.94;
      contrastFactor *= 1.18;
      satFactor *= 0.95;
      hueDegrees += 12;
    }

    if (isNaturalEnhance) {
      brightFactor *= 1.05;
      contrastFactor *= 1.08;
      satFactor *= 1.05;
    }

    if (isBright) {
      brightFactor *= 1.12;
      contrastFactor *= 1.05;
      satFactor *= 1.08;
      hueDegrees += 3;
    }

    if (isClear) {
      contrastFactor *= 1.28;
      brightFactor *= 0.98;
      satFactor *= 1.06;
    }

    const hueMat =
      Math.abs(hueDegrees) > 0.0001 ? getHueRotateMatrix(hueDegrees) : null;

    const imageData = ctx.getImageData(0, 0, width, height);
    const data = imageData.data;
    const len = data.length;

    const pastelTarget = { r: 248, g: 238, b: 245 };

    for (let i = 0; i < len; i += 4) {
      let r = data[i];
      let g = data[i + 1];
      let bCol = data[i + 2];

      r *= brightFactor;
      g *= brightFactor;
      bCol *= brightFactor;

      r = (r - 128) * contrastFactor + 128;
      g = (g - 128) * contrastFactor + 128;
      bCol = (bCol - 128) * contrastFactor + 128;

      if (liftBlacksAmount > 0) {
        const maxLift = 42 * liftBlacksAmount;
        r += (1 - r / 255) * maxLift;
        g += (1 - g / 255) * maxLift;
        bCol += (1 - bCol / 255) * maxLift;
      }

      if (softPastelLiftAmount > 0) {
        const maxLift = 32 * softPastelLiftAmount;
        r += (1 - r / 255) * maxLift;
        g += (1 - g / 255) * maxLift;
        bCol += (1 - bCol / 255) * maxLift;
      }

      const grey = 0.299 * r + 0.587 * g + 0.114 * bCol;
      r = grey + (r - grey) * satFactor;
      g = grey + (g - grey) * satFactor;
      bCol = grey + (bCol - grey) * satFactor;

      if (vibrantPopVibrance > 0) {
        const colourfulness =
          (Math.abs(r - grey) + Math.abs(g - grey) + Math.abs(bCol - grey)) / 3;
        const t = Math.max(0, Math.min(1, (70 - colourfulness) / 70));
        const extraSat = 1 + vibrantPopVibrance * t;

        r = grey + (r - grey) * extraSat;
        g = grey + (g - grey) * extraSat;
        bCol = grey + (bCol - grey) * extraSat;
      }

      if (hueMat) {
        [r, g, bCol] = applyHueRotateMatrix(r, g, bCol, hueMat);
      }

      if (softPastelWashAmount > 0) {
        const mix = softPastelWashAmount;
        r = r * (1 - mix) + pastelTarget.r * mix;
        g = g * (1 - mix) + pastelTarget.g * mix;
        bCol = bCol * (1 - mix) + pastelTarget.b * mix;
      }

      if (isSepia) {
        const origR = r;
        const origG = g;
        const origB = bCol;

        r = 0.393 * origR + 0.769 * origG + 0.189 * origB;
        g = 0.349 * origR + 0.686 * origG + 0.168 * origB;
        bCol = 0.272 * origR + 0.534 * origG + 0.131 * origB;
      }

      if (isBlackAndWhite && !isVintage) {
        const greyBw = 0.299 * r + 0.587 * g + 0.114 * bCol;
        r = g = bCol = greyBw;
      }

      if (isVintage) {
        const origR = r;
        const origG = g;
        const origB = bCol;

        const sepiaR = 0.393 * origR + 0.769 * origG + 0.189 * origB;
        const sepiaG = 0.349 * origR + 0.686 * origG + 0.168 * origB;
        const sepiaB = 0.272 * origR + 0.534 * origG + 0.131 * origB;

        const amount = 0.35;
        r = origR * (1 - amount) + sepiaR * amount;
        g = origG * (1 - amount) + sepiaG * amount;
        bCol = origB * (1 - amount) + sepiaB * amount;
      }

      if (fadedSepiaAmount > 0) {
        const origR = r;
        const origG = g;
        const origB = bCol;

        const sepiaR = 0.393 * origR + 0.769 * origG + 0.189 * origB;
        const sepiaG = 0.349 * origR + 0.686 * origG + 0.168 * origB;
        const sepiaB = 0.272 * origR + 0.534 * origG + 0.131 * origB;

        const amount = fadedSepiaAmount;
        r = origR * (1 - amount) + sepiaR * amount;
        g = origG * (1 - amount) + sepiaG * amount;
        bCol = origB * (1 - amount) + sepiaB * amount;
      }

      data[i] = clamp(r);
      data[i + 1] = clamp(g);
      data[i + 2] = clamp(bCol);
    }

    ctx.putImageData(imageData, 0, 0);
  };

  const handleDownload = async () => {
    const liveCanvas = canvasRef.current;
    if (!liveCanvas) return;

    const exportCanvas = document.createElement('canvas');
    exportCanvas.width = liveCanvas.width;
    exportCanvas.height = liveCanvas.height;
    const ectx = exportCanvas.getContext('2d', { willReadFrequently: true });

    const pxPerCssPx = getPxPerCssPx(liveCanvas);

    const { scaleX, scaleY } = getScaleXY(liveCanvas);
    const blurScale = (scaleX + scaleY) / 2;

    // ✅ Wait for any web fonts used by textItems before drawing
    await ensureFontsReadyForExport(scaleY || pxPerCssPx);

    // Blur base image only
    const sliderBlur = typeof blur === 'number' ? blur : 0;
    const previewBlurCssPx = sliderBlur * BLUR_UI_TO_CSS_MULTIPLIER;
    const exportBlurCanvasPx = previewBlurCssPx * blurScale;

    if (exportBlurCanvasPx > 0.01) {
      ectx.filter = `blur(${exportBlurCanvasPx}px)`;
    }

    // Export MUST draw from the live canvas so the user’s rotate/flip is included
    ectx.drawImage(liveCanvas, 0, 0);

    // Reset filter so stickers/text stay sharp
    ectx.filter = 'none';

    applyFiltersToCanvas(ectx, exportCanvas.width, exportCanvas.height);

    // ✅ Stickers (HTML overlays) → rasterised onto export canvas
    await drawStickerItems(ectx);

    // Text goes on top
    drawTextItems(ectx, liveCanvas);

    hasAddedTextRef.current = false;

    const { mime, quality } = getFormatFromName();

    if (mime === 'image/jpeg') {
      ectx.save();
      ectx.globalCompositeOperation = 'destination-over';
      ectx.fillStyle = '#fff'; // jpeg needs a solid background
      ectx.fillRect(0, 0, exportCanvas.width, exportCanvas.height);
      ectx.restore();
    }

    exportCanvas.toBlob(
      async (blob) => {
        if (!blob) {
          alert('Failed to generate image.');
          return;
        }
        // download for testing
        // downloadImage(blob);

        // filestack
        const res = await sendToFilestack(blob);

        const fileData = getFilestackFileData(res);

        if (fileData) {
          await logFilestackUpload(fileData);
        } else {
          console.error('No Filestack file data found:', res);
        }
      },
      mime,
      quality,
    );
  };

  // eslint-disable-next-line no-unused-vars
  const downloadImage = (blob) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = exportedFilename();
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };
  const sendToFilestack = (blob) => {
    setUploadError(false);

    const onProgress = (evt) => {
      const percent = Math.round(evt.totalPercent || 0);
      if (percent > 0) {
        setUploadProgress(percent);
      }
    };

    if (
      window.filestack &&
      typeof window.filestack.init === 'function' &&
      window.client &&
      typeof window.client.upload === 'function'
    ) {
      uploadStartTimeRef.current = Date.now();
      setUploadProgress(0);
      setDisplayProgress(0);

      // Set to true to test failure UI. Set to false to function normally.
      const uploadPromise = false
        ? Promise.reject(new Error('Test upload failure'))
        : window.client.upload(blob, onProgress, {}, {});

      return uploadPromise
        .then(function (res) {
          console.log('success: ', res);

          if (window.logoImage) {
            // somehow have to determine if logo is being uploaded
            // this doesn't work in the current post and image editor
            // sendlogotopanorama(res.url);
          } else {
            window.JSVnewurl = res.url;
            // TODO: test with Jeff (change form action URL to test)
            sendToPanorama(res.url);
            saveClicked();
          }
          window.parent.filepickeractive = 'false';

          setUploadProgress(100);

          const finishInterval = setInterval(() => {
            setDisplayProgress((prev) => {
              if (prev >= 100) {
                clearInterval(finishInterval);

                setTimeout(() => {
                  setUploadProgress(null);
                  setDisplayProgress(0);
                }, 300);

                return 100;
              }

              return Math.min(prev + 6, 100);
            });
          }, 30);
          return res;
        })
        .catch(function (err) {
          console.log(err);
          const failInterval = setInterval(() => {
            setDisplayProgress((prev) => {
              if (prev >= 100) {
                clearInterval(failInterval);

                setUploadError(true);

                setTimeout(() => {
                  setUploadProgress(null);
                  setDisplayProgress(0);
                  setUploadError(false);
                }, 1200);

                return 100;
              }

              return Math.min(prev + 8, 100);
            });
          }, 30);
        });
    } else {
      console.warn('Third-party script method is not available yet');
      return null;
    }
  };

  return (
    <>
      {uploadProgress !== null && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60">
          <div className="w-[90%] max-w-sm rounded-2xl bg-white p-6 text-center shadow-2xl">
            <p
              className={`mb-4 text-lg font-semibold ${
                uploadError ? 'text-red-600' : 'text-gray-800'
              }`}
            >
              {uploadError ? 'Upload failed' : 'Uploading image...'}
            </p>

            <div className="h-4 w-full overflow-hidden rounded-full bg-gray-200">
              <div
                className={`h-full rounded-full transition-all duration-300 ${
                  uploadError ? 'bg-red-500' : 'bg-blue-500'
                }`}
                style={{ width: `${displayProgress}%` }}
              />
            </div>

            <p className="mt-3 text-sm font-medium text-gray-600">
              {uploadError ? 'Please try again' : `${displayProgress}%`}
            </p>
          </div>
        </div>
      )}
      <button
        ref={buttonRef}
        className="border border-md-beige px-2 py-1 rounded-xl hover:bg-md-beige transition duration-300"
        onClick={() => {
          const go = () => handleDownload();
          if (typeof requestRotateExit === 'function') requestRotateExit(go);
          else go();
        }}
      >
        Upload Image
      </button>

      <form
        name="processphotos"
        method="post"
        target="updatenow"
        action="cgi-bin/Panorama.cgi?RescueShelter0~Photo"
        className="absolute"
      >
        <input type="hidden" name="overwritephotoid" />
        <input type="hidden" name="addphotoid" />
        <input type="hidden" name="code" />
        <input type="hidden" name="id" />
        <input type="hidden" name="password" />
        <input type="hidden" name="photocodes" />
        <input type="hidden" name="pic" />
      </form>
    </>
  );
}
